import classNames from 'classnames';
import React from 'react';

import {FunnelLogContextProvider} from '@udemy/funnel-tracking';
import {FeatureVariantAssignment} from '@udemy/graphql';
import {
    CertificationBundleUnit,
    CertificationExamVoucherBannerWrapper,
    VoucherUIRegion,
    CertificationVoucherBundleUnitCarousel,
} from '@udemy/react-certification-preparation';
import {
    HorizontalBundleUnit,
    HorizontalBundleUnitProductCard,
    SkeletonCourseCarousel,
    SkeletonTitle,
} from '@udemy/react-discovery-units';
import {ContentArea} from '@udemy/react-layout-components';

import {isMwebStandardizedMobileCourseCardsEnabled} from 'src/lib/experimentation-helper';

import {BUNDLE_DISCOUNT_CODE, SPECIAL_VOUCHER_BUNDLE_DISCOUNT_PROPS} from '../constants';
import styles from '../topic.module.less';
import {type BundleVariant, type Topic, BUNDLE_VARIANT} from '../types/topic';

interface TopicBundleUnitProps {
    variant: BundleVariant;
    topicId: Topic['id'];
    isTopicVoucherModuleAboveBundle?: FeatureVariantAssignment;
    isMobile?: boolean | null;
    topicCatSubcatMwebCardStandardizationFeature?: FeatureVariantAssignment;
    useVerticalRecoCourseCardStyle?: boolean;
    /** When true, defer generic bundle layout until Tapen resolves — avoids condensed→large flash */
    isExperimentConfigsLoading?: boolean;
}

export const TopicBundleUnit: React.FC<TopicBundleUnitProps> = ({
    variant,
    topicId,
    isTopicVoucherModuleAboveBundle,
    isMobile,
    topicCatSubcatMwebCardStandardizationFeature,
    useVerticalRecoCourseCardStyle,
    isExperimentConfigsLoading,
}) => {
    const voucherAbove =
        isTopicVoucherModuleAboveBundle?.configuration?.isVoucherModuleAbovebundle ?? false;

    const voucherUiRegion = (
        isTopicVoucherModuleAboveBundle?.configuration?.isTopicVoucherModuleAbovebundle
            ? 'above_course_bundle'
            : 'below_course_bundle'
    ) as VoucherUIRegion;

    const voucherBanner = (
        <CertificationExamVoucherBannerWrapper
            topicId={topicId}
            theme="dark"
            layout="horizontal"
            isTopicBanner
            experimentData={isTopicVoucherModuleAboveBundle}
            uiRegion={voucherUiRegion}
            showAddToCart
        />
    );

    const voucherContainerClass = classNames(styles['voucher-banner-bundle-unit-container'], {
        [styles['voucher-above']]: voucherAbove,
        [styles['voucher-below']]: !voucherAbove,
    });

    switch (variant.type) {
        case BUNDLE_VARIANT.CERTIFICATION:
            return (
                <div className={voucherContainerClass}>
                    {variant.showCertificationVoucherBundleUnit ? (
                        <FunnelLogContextProvider pageType="topic">
                            <CertificationVoucherBundleUnitCarousel
                                units={variant.certificationBundles.map((bundle) => ({
                                    bundle: bundle,
                                    showCommonInstructor: true,
                                    ...SPECIAL_VOUCHER_BUNDLE_DISCOUNT_PROPS,
                                }))}
                            />
                        </FunnelLogContextProvider>
                    ) : (
                        <div
                            className={classNames(
                                styles['certification-bundle-unit-wrapper'],
                                'component-margin',
                            )}
                            data-item-index={0}
                        >
                            <CertificationBundleUnit
                                certificationBundle={variant.certificationBundles[0]}
                                isBundleDiscountEnabled
                                bundleDiscountCode={BUNDLE_DISCOUNT_CODE}
                            />
                        </div>
                    )}
                    {voucherBanner}
                </div>
            );
        case BUNDLE_VARIANT.GENERIC: {
            if (isMobile && isExperimentConfigsLoading) {
                return (
                    <>
                        <div
                            className={classNames(
                                styles['certification-bundle-unit-wrapper'],
                                'component-margin',
                            )}
                            data-item-index={0}
                            data-purpose="generic-bundle-experiment-pending"
                            data-testid="generic-bundle-experiment-pending"
                        >
                            <SkeletonTitle className={styles['title-skeleton']} />
                            <SkeletonCourseCarousel courseCount={3} />
                        </div>
                        <div className={styles['voucher-banner-generic-bundle-unit']}>
                            {voucherBanner}
                        </div>
                    </>
                );
            }
            const mwebStandardizedLargeBundleCards =
                !!isMobile &&
                isMwebStandardizedMobileCourseCardsEnabled(
                    topicCatSubcatMwebCardStandardizationFeature?.configuration,
                );
            const useLargeProductCardBundle =
                useVerticalRecoCourseCardStyle === true || mwebStandardizedLargeBundleCards;
            const genericBundleTitle = interpolate(
                gettext("Looking to advance your skills in %(topicName)s? We've got you."),
                {topicName: variant.topicName},
                true,
            );
            const genericBundleSubtitle = gettext(
                'Get everything you need to reach your goals in one convenient bundle.',
            );
            const genericBundleValueProps = [
                gettext('Top-rated courses'),
                gettext('Popular with learners just like you'),
                gettext('Guidance from real-world experts'),
            ];
            const genericBundleDiscountSpread = variant.enableSpecialBundlePricing
                ? {
                      isBundleDiscountEnabled: true,
                      bundleDiscountCode: BUNDLE_DISCOUNT_CODE,
                  }
                : {};

            return (
                <>
                    <div
                        className={classNames(
                            styles['certification-bundle-unit-wrapper'],
                            'component-margin',
                        )}
                        data-item-index={0}
                    >
                        {useLargeProductCardBundle ? (
                            <ContentArea>
                                <HorizontalBundleUnitProductCard
                                    stackedCourseCardLayout
                                    contents={variant.courses}
                                    title={genericBundleTitle}
                                    subtitle={genericBundleSubtitle}
                                    valueProps={genericBundleValueProps}
                                    courseCardProps={{isAsyncStaticPrice: true}}
                                    {...genericBundleDiscountSpread}
                                />
                            </ContentArea>
                        ) : (
                            <HorizontalBundleUnit
                                contents={variant.courses}
                                title={genericBundleTitle}
                                subtitle={genericBundleSubtitle}
                                valueProps={genericBundleValueProps}
                                {...genericBundleDiscountSpread}
                            />
                        )}
                    </div>
                    <div className={styles['voucher-banner-generic-bundle-unit']}>
                        {voucherBanner}
                    </div>
                </>
            );
        }
        case BUNDLE_VARIANT.VOUCHER_ONLY:
            return <div className={voucherContainerClass}>{voucherBanner}</div>;
    }
};
